import React, { useCallback } from 'react'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'
import MailIcon from '@mui/icons-material/Mail'

import Pin from 'pages/shared/Pin'
import DeviceActivityRow from './DeviceActivityRow'

import { customerDisplayName, fullOneLineAddress } from 'helpers/formatters'
import GA4 from 'helpers/ga4'
import { color } from 'styles/colors'
import { Customer } from 'types/DeviceActivity'

  type Props = {
    customer: Customer
    isSelected: boolean
    onSelectCallback: (customerId: string | null) => void
  }

const CustomerInfoRow = ({
  customer,
  isSelected,
  onSelectCallback
}: Props) => {
  const { firstName = '', lastName = '', companyName = '', devices, email, customerId } = customer ?? {}
  const displayName = customerDisplayName(
    firstName ?? '',
    lastName ?? '',
    companyName ?? ''
  )
  const hasAddress =
  customer?.address1 ||
  customer?.city ||
  customer?.state ||
  customer?.zip

  const sendGA4Event = (gaAction: string) => {
    const ga4Event = {
      category: 'Dashboard Page',
      action: gaAction
    }

    GA4.sendEvent(ga4Event)
  }

  const selectCustomer = useCallback(() => {
    onSelectCallback(customerId)
    sendGA4Event('Select Customer Card 24hrs')
  }, [customerId, onSelectCallback])

  const onClickCustomerLink = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation()
    sendGA4Event('Click Customer 24hrs')
  }

  const onClickEmailLink = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation()
  }

  return (
    <S.Container
      onClick={selectCustomer}
      $isSelected={isSelected}
      data-testid="customer-activity-row"
    >
      <S.HeaderRow>
        <NavLink to={`/customers/${customerId}/systems/dashboard`} onClick={onClickCustomerLink}>
          <S.Name data-testid="customer-name">
            {displayName}
          </S.Name>
        </NavLink>
        <S.Actions>
          <S.Pin
            isPinned={customer.pinned}
            customerName={displayName ?? ''}
            customerId={customer.customerId}
            pinnedAt={customer.pinnedAt}
            displayUnpinned
          />

          {email && (
            <S.Email href={`mailto:${email}`} target="_blank" rel="noreferrer" onClick={onClickEmailLink}><MailIcon fontSize="medium" color={'disabled'} />
            </S.Email>)
          }

        </S.Actions>
      </S.HeaderRow>

      <S.ContentRow>
        <S.BottomRow>
          <S.Devices>
            {devices.map((device) => (
              <DeviceActivityRow key={device.deviceId} device={device} type={device.deviceType} customerId={customer.customerId} />
            ))}
          </S.Devices>
          {hasAddress && (
            <S.Address data-testid="customer-address">
                {fullOneLineAddress(
                  [customer.address1, customer.address2].filter(Boolean).join(', '),
                  customer.city,
                  customer.state,
                  customer.zip
                )}
            </S.Address>
          )}
        </S.BottomRow>
      </S.ContentRow>
    </S.Container>
  )
}

const S = {
  Container: styled.div<{ $isSelected?: boolean }>`
    display: flex;
    flex-direction: column;
    padding: 1rem 0.75rem;
    border: 1px solid ${({ $isSelected }) => $isSelected ? color('dashboardCardHoverBorder') : color('dashboardCardBorder')};
    border-radius: 1rem;
    gap: 8px;
    margin-bottom: 8px;
    cursor: pointer;
    background-color: ${({ $isSelected }) => $isSelected ? color('dashboardCardHoverBorder') + '14' : 'transparent'};
  `,

  HeaderRow: styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,

  Name: styled.div`
    font-weight: 600;
    color: ${color('primaryText')};
    &:hover{
      color: ${color('dashboardCardHoverBorder')};
    }
  `,

  Actions: styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
  `,

  ContentRow: styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  `,
  BottomRow: styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
  `,
  Devices: styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  `,

  Address: styled.div`
    flex: 30%;
    font-size: 14px;
    text-align: right;
  `,

  Pin: styled(Pin)`
    position: relative;
    top: 1px;
  `,
  Email: styled.a`
    position: relative;
    top: -2px;
    i{
      color: ${color('deviceTooltipLabelText')};
    }
  `
}

export default CustomerInfoRow
